import { useTranslation } from 'react-i18next';
import { Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { AuditLog, PaginationInfo } from '../hooks/useAuditLogs';

interface AuditLogTableProps {
  logs: AuditLog[];
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}

export function AuditLogTable({ logs, pagination, onPageChange }: AuditLogTableProps) {
  const { t } = useTranslation();

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatAction = (action: string) => {
    // Convert snake_case or camelCase to readable format
    return action
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium">{t('audit.timestamp')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium">{t('audit.user')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium">{t('audit.action')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium">{t('audit.resource')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium">{t('audit.ipAddress')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium">{t('audit.details')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {t('audit.noLogs')}
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      {log.phiAccessed && (
                        <span title={t('audit.phiAccess')}>
                          <Shield className="h-4 w-4 text-amber-500" />
                        </span>
                      )}
                      {formatTimestamp(log.timestamp)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium">{log.userName || log.userEmail}</p>
                      {log.userName && (
                        <p className="text-xs text-muted-foreground">{log.userEmail}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-xs font-medium">
                      {formatAction(log.action)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium">{log.resourceType}</p>
                      {log.resourceId && (
                        <p className="text-xs text-muted-foreground font-mono">{log.resourceId}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
                    {log.ipAddress || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {log.metadata ? (
                      <details className="cursor-pointer">
                        <summary className="text-primary hover:underline">
                          {t('audit.details')}
                        </summary>
                        <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-w-xs">
                          {JSON.stringify(log.metadata, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
            {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
